import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {AbstractFieldInputTypeHandlerComponent} from '../../../../type-handler/abstract-field-input-type-handler.component';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {SelectElement} from '../../../../../../select/select.model';
import {
  FormDefinitionFieldModel
} from '../../../../../../../../modules/form/definition/field/api/entity/formDefinitionField.model';
import {FormValueModel} from '../../../../../../../../modules/form/form/value/api/entity/formValue.model';
import {
  FormFieldDefinitionOptionModel
} from '../../../../../../../../modules/form/definition/field/api/entity/formFieldDefinitionOption.model';

@Component({
  selector: 'app-drawable-image-field-input-type-handler',
  templateUrl: './drawable-image-field-input-type-handler.component.html',
})
export class DrawableImageFieldInputTypeHandlerComponent extends AbstractFieldInputTypeHandlerComponent<UntypedFormControl>
  implements AfterViewInit {

  public static resolveReadableValue(value: any): Promise<string> {
    return Promise.resolve(value as string);
  }

  readonly MAX_HISTORY_SIZE = 30;

  formGroup: UntypedFormGroup;
  selectedOption: string;
  selectedColor = '#000000';
  shapeOptions: FormFieldDefinitionOptionModel[];

  canvasContext: CanvasRenderingContext2D;
  isDrawing = false;
  historyStates = [];
  height = 0;
  width = 0;
  image: HTMLImageElement;

  @ViewChild('canvas', { static: true })
  canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('container') container: ElementRef<HTMLDivElement>;

  constructor(private formBuilder: UntypedFormBuilder) {
    super();
    this.formGroup = this.formBuilder.group({
      thickness: this.formBuilder.control(2),
      size: this.formBuilder.control(20),
      fill: this.formBuilder.control(false),
    });
  }

  ngAfterViewInit() {
    this.canvasContext = this.canvasElement.getContext('2d');
  }

  buildJsonControl(formDefinitionField: FormDefinitionFieldModel): UntypedFormControl {
    const control = this.formBuilder.control(undefined, formDefinitionField.required ? Validators.required : []);
    if (formDefinitionField.defaultValue) {
      this.setCanvasImage(formDefinitionField.defaultValue);

    }
    if (formDefinitionField.options?.length > 0) {
      this.shapeOptions = this.formDefinitionField.options.filter(value => this.isShape(value.label));
      this.selectedOption = formDefinitionField.options[0].label;
    }
    return control;
  }

  initiateForm(formValue: FormValueModel): void {
    if (formValue?.value) {
      this.setCanvasImage(formValue.value);
    }
  }

  synchronizeValue(): string {
    return this.jsonControl.value;
  }

  mouseDown(e) {
    if (this.selectedOption === 'draw') {
      const coords: number[] = this.convertXYToCanvasCoords(e.clientX, e.clientY);
      this.canvasContext.beginPath();
      this.canvasContext.lineWidth = this.thickness;
      this.canvasContext.strokeStyle = this.selectedColor;
      this.canvasContext.lineJoin = 'round';
      this.canvasContext.lineCap = 'round';
      this.canvasContext.moveTo(coords[0], coords[1]);
      this.saveToHistory();
      this.isDrawing = true;
      this.synchronizeCanvasWithControl();
    }
  }

  mouseMove(e) {
    if (this.isDrawing === true) {
      const coords: number[] = this.convertXYToCanvasCoords(e.clientX, e.clientY);
      this.canvasContext.lineTo(coords[0], coords[1]);
      this.canvasContext.stroke();
      this.synchronizeCanvasWithControl();
    }
  }

  mouseUp() {
    if (this.isDrawing === true) {
      this.isDrawing = false;
      this.canvasContext.closePath();
      this.synchronizeCanvasWithControl();
    }
  }

  mouseDbClick(e) {
    if (this.selectedOption && this.selectedOption !== 'draw') {
      this.saveToHistory();
      const coords: number[] = this.convertXYToCanvasCoords(e.clientX, e.clientY);
      if (this.isShape(this.selectedOption)) {
        this.canvasContext.beginPath();
        this.canvasContext.lineWidth = this.thickness;
        this.canvasContext.strokeStyle = this.selectedColor;
        if (this.selectedOption === 'circle') {
          this.canvasContext.arc(coords[0], coords[1], this.size / 2, 0, 2 * Math.PI);
        } else if (this.selectedOption === 'square') {
          this.canvasContext.rect(coords[0] - this.size / 2, coords[1] - this.size / 2, this.size, this.size);
        } else if (this.selectedOption === 'triangle') {
          this.canvasContext.moveTo(coords[0] - this.size / 2, coords[1] + this.size / 2 );
          this.canvasContext.lineTo(coords[0], coords[1] - this.size / 2);
          this.canvasContext.lineTo(coords[0] + this.size / 2, coords[1] + this.size / 2);
          this.canvasContext.closePath();
        } else if (this.selectedOption === 'cross') {
          this.canvasContext.moveTo(coords[0] - this.size / 2, coords[1] + this.size / 2 );
          this.canvasContext.lineTo(coords[0] + this.size / 2, coords[1] - this.size / 2);

          this.canvasContext.moveTo(coords[0] - this.size / 2, coords[1] - this.size / 2 );
          this.canvasContext.lineTo(coords[0] + this.size / 2, coords[1] + this.size / 2);
        }
        this.canvasContext.stroke();

        if (this.fill === true) {
          this.canvasContext.fillStyle = this.selectedColor;
          this.canvasContext.fill();
        }
      } else {
        this.addImageToCanvas(this.selectedOption,
            coords[0], coords[1]);
      }
      this.synchronizeCanvasWithControl();
    }

  }

  optionSelected(optionLabel: string) {
    this.selectedOption = optionLabel;
  }

  synchronizeCanvasWithControl(imageContent?: any) {
    this.jsonControl.setValue(this.canvasElement.toDataURL());
    // console.log(['synchronizeCanvasWithControl', this.jsonControl.value, imageContent]);
  }

  undo() {
    // console.log(this.historyStates.length);
    if (this.historyStates.length > 0) {
      const restoreState = this.historyStates.pop();
      this.setCanvasImage(restoreState);
    }
  }

  setCanvasImage(imageContent: any) {
    this.image = new Image();
    this.image.src = imageContent;
    this.image.onload = (event) => {
      this.height = this.image.height;
      this.width = this.image.width;
      this.canvasElement.height = this.height;
      this.canvasElement.width = this.width;
      this.canvasContext.clearRect(0, 0, this.image.width, this.image.height);
      this.canvasContext.drawImage(this.image, 0, 0, this.image.width, this.image.height);
      this.synchronizeCanvasWithControl(imageContent);
      // console.log(['canvasElement', this.canvasElement.height, this.canvasElement.width, this.container.nativeElement]);
    };
  }

  addImageToCanvas(imageContent, x: number, y: number) {
    const image = new Image();
    image.src = imageContent;
    image.onload = () => {
      x = x - image.width / 2;
      y = y - image.height / 2;
      this.canvasContext.drawImage(image, x, y);
    };
  }

  convertXYToCanvasCoords(x: number, y: number): number[] {
    const rect = this.canvasElement.getBoundingClientRect();
    // console.log([x, y, rect, this.image.height, this.image.width]);
    return [
        x - rect.left,
        y - rect.top
    ];
  }

  get canvasElement(): HTMLCanvasElement {
    return this.canvas.nativeElement;
  }

  get thickness(): number {
    return this.formGroup.get('thickness').value;
  }

  get fill(): boolean {
    return this.formGroup.get('fill').value;
  }

  get size(): number {
    return this.formGroup.get('size').value;
  }

  resolveShapeIconName(value: string): string {
    switch (value) {
      case 'triangle':
        return 'triangle';
      case 'square':
        return 'square';
      case 'circle':
        return 'circle';
      case 'cross':
        return 'x';
    }
    return undefined;
  }

  isShape(value: string): boolean {
    return value === 'triangle' || value === 'square' || value === 'circle' || value === 'cross';
  }

  saveToHistory() {
    this.historyStates.push(this.canvasElement.toDataURL());
    if (this.historyStates.length > this.MAX_HISTORY_SIZE) {
      this.historyStates.shift();
    }
  }

}
